// Presentational: grid unificado de cards de white label (Configurações).
// Exibe TODOS os WLs do portal — os já configurados como card com link
// pro detalhe e os "não configurados" (só no portal) em estado neutro
// tracejado com CTA de criação da configuração padrão.
import {
  CircleDashedIcon,
  SparklesIcon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { FRONTEND_ROUTES } from "@/constants"
import { cn } from "@/lib/utils"

import { useCreateDefaultConfiguration } from "../configuration-mutations"
import type { MergedWhiteLabelItem } from "../merge-white-labels"

const listPrefix =
  "pages.notificationCenter.configurationsPage"
const defaultConfigPrefix =
  "pages.notificationCenter.configurationsDetailPage.defaultConfig"

// Chip SOFT semântico (nunca fundo sólido).
const softChipClasses = (active: boolean) =>
  cn("rounded-full border font-medium", {
    "border-emerald-200 bg-emerald-50 text-emerald-700":
      active,
    "border-gray-200 bg-gray-50 text-gray-700": !active,
  })

// Card de WL JÁ configurado — link pro detalhe (comportamento atual).
const ConfiguredCard = ({
  item,
}: {
  item: Extract<
    MergedWhiteLabelItem,
    { status: "configured" }
  >
}) => {
  const { t } = useTranslation()
  const { whiteLabel } = item

  return (
    <Link
      to={`${FRONTEND_ROUTES.NOTIFICATION_CENTER.href}/configurations/${item.id}`}
    >
      <Card className="h-full transition-colors hover:border-primary/40">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="truncate">
              {item.name}
            </CardTitle>
            <Badge
              variant="outline"
              className={softChipClasses(whiteLabel.active)}
            >
              {whiteLabel.active
                ? t(`${listPrefix}.active`)
                : t(`${listPrefix}.inactive`)}
            </Badge>
          </div>
          <CardDescription className="font-mono text-xs">
            {item.id}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="flex items-center justify-between text-sm font-semibold">
            {t(`${listPrefix}.channels`)}
            <span className="text-xs font-normal tabular-nums text-muted-foreground">
              {whiteLabel.channels.length}
            </span>
          </p>
          {whiteLabel.channels.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {t(`${listPrefix}.noChannels`)}
            </p>
          )}
          {whiteLabel.channels.map((channel) => (
            <div
              key={channel.id}
              className={cn(
                "flex items-center justify-between rounded-md border p-2 text-sm",
                {
                  "border-emerald-200 bg-emerald-50 text-emerald-700":
                    channel.active,
                  "border-gray-200 bg-gray-50 text-gray-700":
                    !channel.active,
                }
              )}
            >
              <span>
                {channel.description ?? channel.name}
              </span>
              <span className="text-xs font-medium uppercase">
                {channel.name}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>
    </Link>
  )
}

// Card de WL SEM configuração — estado neutro tracejado + CTA de
// criação da configuração padrão (AlertDialog → POST → refetch).
const UnconfiguredCard = ({
  item,
  onConfigured,
}: {
  item: Extract<
    MergedWhiteLabelItem,
    { status: "unconfigured" }
  >
  onConfigured: () => void | Promise<void>
}) => {
  const { t } = useTranslation()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const createDefaultConfiguration =
    useCreateDefaultConfiguration(item.id, {
      onSuccess: onConfigured,
    })

  return (
    <>
      <Card
        data-testid={`wl-unconfigured-${item.id}`}
        className="flex h-full flex-col border-dashed bg-muted/30"
      >
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="truncate text-muted-foreground">
              {item.name}
            </CardTitle>
            <Badge
              variant="outline"
              className="rounded-full border-zinc-200 bg-zinc-50 font-medium text-zinc-500"
            >
              {t(`${listPrefix}.notConfiguredBadge`)}
            </Badge>
          </div>
          <CardDescription className="flex items-center gap-1.5">
            <CircleDashedIcon
              className="size-3.5 shrink-0 text-zinc-400"
              aria-hidden="true"
            />
            {t(`${listPrefix}.notConfiguredHint`)}
          </CardDescription>
        </CardHeader>
        <CardContent className="mt-auto">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={createDefaultConfiguration.isPending}
            onClick={() => setIsDialogOpen(true)}
          >
            <SparklesIcon
              className="mr-1.5 size-4"
              aria-hidden="true"
            />
            {t(`${defaultConfigPrefix}.ctaAction`)}
          </Button>
        </CardContent>
      </Card>

      <AlertDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(`${defaultConfigPrefix}.dialogTitle`)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                `${defaultConfigPrefix}.dialogDescription`
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(`${defaultConfigPrefix}.cancel`)}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                createDefaultConfiguration.mutate()
              }
            >
              {t(`${defaultConfigPrefix}.confirm`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export const WhiteLabelMergedCards = ({
  items,
  onConfigured,
}: {
  items: MergedWhiteLabelItem[]
  onConfigured: () => void | Promise<void>
}) => {
  const { t } = useTranslation()

  return (
    <section className="space-y-4">
      <h2 className="font-semibold">
        {t(`${listPrefix}.whiteLabels`)}
      </h2>
      <div className="grid gap-6 @xl:grid-cols-2 @5xl:grid-cols-4">
        {items.map((item) =>
          item.status === "configured" ? (
            <ConfiguredCard key={item.id} item={item} />
          ) : (
            <UnconfiguredCard
              key={item.id}
              item={item}
              onConfigured={onConfigured}
            />
          )
        )}
      </div>
    </section>
  )
}
